import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, Printer } from "lucide-react";
import { api, API, apiError } from "@/lib/api";
import { fmtDate, fmtGNF } from "@/lib/format";
import { Loading } from "@/components/ProtectedRoute";

export default function Receipt() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api
      .get(`/orders/${id}`)
      .then((r) => setOrder(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [id]);

  if (!order) return <Loading label="Préparation du bon de livraison…" />;

  const openPdf = () => window.open(`${API}/orders/${id}/receipt`, "_blank");

  return (
    <div className="mx-auto max-w-3xl px-4 py-6" data-testid="receipt-page">
      <div className="mb-4 flex flex-wrap gap-2 print:hidden">
        <button type="button" onClick={() => window.print()} className="ds-btn-primary" data-testid="receipt-print-button">
          <Printer className="h-4 w-4" /> Imprimer
        </button>
        <button type="button" onClick={openPdf} className="ds-btn-ghost" data-testid="receipt-pdf-button">
          <Download className="h-4 w-4" /> Télécharger le PDF
        </button>
      </div>

      <div className="ds-card overflow-hidden print:border-0 print:shadow-none">
        <div className="flex flex-wrap items-center justify-between gap-2 bg-brand px-5 py-4 text-white print:bg-brand">
          <div>
            <p className="font-display text-lg font-extrabold">EMPIRE-SHOP</p>
            <p className="text-[11px] text-emerald-100">Entrepôt EMPIRE-SHOP · Guinée</p>
          </div>
          <div className="text-right">
            <p className="font-display whitespace-nowrap text-xs font-bold sm:text-sm">BON DE LIVRAISON</p>
            <p className="font-num text-xs text-emerald-100">{order.number}</p>
          </div>
        </div>

        <div className="grid gap-4 p-5 sm:grid-cols-2">
          <div className="space-y-1 text-sm">
            <p className="ds-label">Client</p>
            <p className="font-semibold text-slate-900">{order.customer_name}</p>
            <p className="font-num text-slate-600">{order.phone}</p>
            <p className="text-slate-600">{order.address}</p>
          </div>
          <div className="space-y-1 text-sm sm:text-right">
            <p className="ds-label">Livraison</p>
            <p className="text-slate-800">{order.zone_name} {order.zone_eta ? `· ${order.zone_eta}` : ""}</p>
            <p className="text-slate-600">{order.delivery_mode === "pickup" ? "Retrait à l'entrepôt" : "Livraison à domicile"}</p>
            {order.delivery_mode === "pickup" && order.pickup_date && (
              <p className="text-slate-600" data-testid="receipt-pickup-slot">
                Retrait prévu : {order.pickup_date} · {order.pickup_slot}
              </p>
            )}
            {order.delivery_mode === "pickup" && order.pickup_code && (
              <p className="font-num text-slate-800" data-testid="receipt-pickup-code">
                Code de retrait : <span className="font-bold tracking-widest">{order.pickup_code}</span>
              </p>
            )}
            <p className="text-slate-600">{fmtDate(order.created_at)}</p>
            <p className="text-slate-800">
              {order.payment_method_label} — <span className="font-semibold">{order.payment_status}</span>
            </p>
            <p className="text-slate-800">Statut : <span className="font-semibold">{order.status}</span></p>
          </div>
        </div>

        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-5 py-2">Produit</th>
              <th className="px-2 py-2">SKU</th>
              <th className="px-2 py-2 text-right">Qté</th>
              <th className="px-2 py-2 text-right">P.U.</th>
              <th className="px-5 py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100">
                <td className="px-5 py-2 text-slate-900">{it.product_name}</td>
                <td className="font-num px-2 py-2 text-slate-500">{it.sku}</td>
                <td className="font-num px-2 py-2 text-right">{it.quantity}</td>
                <td className="font-num px-2 py-2 text-right">{fmtGNF(it.unit_price)}</td>
                <td className="font-num px-5 py-2 text-right font-semibold">{fmtGNF(it.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="space-y-1 border-t border-slate-200 px-5 py-4 text-sm">
          <div className="flex justify-between"><span className="text-slate-600">Sous-total</span><span className="font-num">{fmtGNF(order.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-slate-600">Frais de livraison</span><span className="font-num">{fmtGNF(order.delivery_fee)}</span></div>
          <div className="flex justify-between border-t border-slate-200 pt-2">
            <span className="font-display font-bold text-slate-900">TOTAL À PAYER</span>
            <span className="font-num text-lg font-black text-brand" data-testid="receipt-total">{fmtGNF(order.total)}</span>
          </div>
        </div>

        {order.note && <p className="px-5 pb-3 text-xs text-slate-600">Note : {order.note}</p>}

        <div className="grid gap-6 border-t border-slate-200 px-5 py-6 text-xs text-slate-500 sm:grid-cols-2">
          <p>Signature préparateur<br /><br />______________________</p>
          <p className="sm:text-right">Signature client<br /><br />______________________</p>
        </div>
      </div>
    </div>
  );
}
